import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, getDictDefault } from '../contexts/LanguageContext';
import { CaretLeft, FloppyDisk, Crown, DotsSixVertical, X as XIcon, CreditCard, Camera, Bank, Coins } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import DetailingTab from '../components/DetailingTab';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default order of CMS text keys editable in the Rolet tab.
const DEFAULT_TEXT_KEYS = [
  'mainSlogan',
  'counterTemplate',
  'stayInCar',
  'vipShareInstruction',
  'vipNoStoryNoCard',
  'vipDisclaimer',
  'plateRecognition',
  'unusedWashesNotice',
  // Referral steps
  'referralStep1Title', 'referralStep1Desc',
  'referralStep2Title', 'referralStep2Desc',
  'referralStep3Title', 'referralStep3Desc',
  'referralStep4Title', 'referralStep4Desc',
  // Referral shareable banner
  'referralBannerText',
];

// Iter33 — customer-only app: only the toggles that still gate a live API / Admin tab.
const MANAGER_PERMS = ['view_customers', 'view_marketing', 'view_subscribers', 'delete_users'];
const EMPLOYEE_PERMS = ['register_cash_subscription', 'cancel_subscription'];

// Reusable toggle pill
function Toggle({ on, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative w-11 h-6 rounded-full transition shrink-0 ${on ? 'bg-[#0ABAB5]' : 'bg-white/15'}`}
      data-testid={testid}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition ${on ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

function PermToggleRow({ label, enabled, color, testid, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full p-2.5 rounded-xl bg-white/5 flex items-center justify-between text-xs"
      data-testid={testid}
    >
      <span className="text-white/80 text-left">{label}</span>
      <span className={`relative w-9 h-5 rounded-full transition ${enabled ? color : 'bg-white/15'}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
      </span>
    </button>
  );
}

export default function OwnerConsolePage() {
  const { isSuperAdmin } = useAuth();
  const { t, setCmsOverrides } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  // CMS texts (shown in Rolet tab)
  const [cmsRows, setCmsRows] = useState({});
  const [textOrder, setTextOrder] = useState(DEFAULT_TEXT_KEYS);
  const [editingKey, setEditingKey] = useState(null);
  const [draggedKey, setDraggedKey] = useState(null);
  // Roles tab
  const [perms, setPerms] = useState({ manager: {}, employee: {} });
  // VIP tab
  const [vipThreshold, setVipThreshold] = useState(7000);
  const [vipRewardProgram, setVipRewardProgram] = useState('prog_standard');
  // Iter35 — minimum shown by the weekly-wash counter (Rolet tab)
  const [counterFloor, setCounterFloor] = useState(723);
  // Payments: Awoara / ANPR / POS Bankar
  const [integrations, setIntegrations] = useState({
    awoara: { enabled: false, merchant_id: '', api_key: '', webhook_url: '' },
    anpr: { enabled: false, camera_ip: '', api_key: '', endpoint_url: '' },
    pos_bankar: { enabled: false, bank_name: '', terminal_id: '', merchant_id: '', host_ip: '', port: 0 },
  });
  // Seats configurable per plan (Task 18 — iter13 wave3)
  const [seats, setSeats] = useState([]);
  // Raiffeisen Gateway
  const [raiffeisenEnabled, setRaiffeisenEnabled] = useState(false);
  const [raiffeisenMerchant, setRaiffeisenMerchant] = useState('');
  const [raiffeisenApiKey, setRaiffeisenApiKey] = useState('');
  const [raiffeisenKeySet, setRaiffeisenKeySet] = useState(false);
  const [raiffeisenWebhook, setRaiffeisenWebhook] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cmsRes, permsRes, settingsRes, intRes, seatsRes, raifRes] = await Promise.all([
        axios.get(`${API}/wash-programs`),
        axios.get(`${API}/cms/texts`),
        axios.get(`${API}/owner/role-permissions`, { withCredentials: true }),
        axios.get(`${API}/settings/public`),
        axios.get(`${API}/owner/integrations`, { withCredentials: true }),
        axios.get(`${API}/subscription-seats`).catch(() => ({ data: [] })),
        axios.get(`${API}/owner/raiffeisen-config`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setPrograms(pRes.data);
      setCmsRows(cmsRes.data || {});
      setPerms(permsRes.data);
      setVipThreshold(settingsRes.data.vip_threshold);
      setVipRewardProgram(settingsRes.data.vip_reward_program);
      setCounterFloor(settingsRes.data.counter_floor ?? 723);
      setSeats(seatsRes.data || []);
      setRaiffeisenEnabled(!!raifRes.data?.raiffeisen_enabled);
      setRaiffeisenMerchant(raifRes.data?.raiffeisen_merchant_id || '');
      setRaiffeisenKeySet(!!raifRes.data?.raiffeisen_api_key_set);
      setRaiffeisenWebhook(raifRes.data?.raiffeisen_webhook_url || '');
      setIntegrations({
        awoara: { ...intRes.data.awoara, api_key: '' },
        anpr: { ...intRes.data.anpr, api_key: '' },
        pos_bankar: { ...intRes.data.pos_bankar },
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load console');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    fetchAll();
  }, [isSuperAdmin, navigate, fetchAll]);

  // ===== Save handlers =====
  const saveCmsText = useCallback(async (key, lang, value) => {
    try {
      const langField = lang === 'sq' ? 'value_sq' : lang === 'it' ? 'value_it' : 'value_en';
      await axios.put(`${API}/cms/texts`, { key, [langField]: value }, { withCredentials: true });
      setCmsRows((prev) => {
        const row = { ...(prev[key] || {}) };
        row[langField] = value;
        const next = { ...prev, [key]: row };
        setCmsOverrides(next);
        return next;
      });
      toast.success(t('autoSaved'));
    } catch { toast.error('Failed'); }
  }, [t, setCmsOverrides]);

  const resetCmsText = useCallback(async (key) => {
    try {
      await axios.delete(`${API}/cms/texts/${key}`, { withCredentials: true });
      setCmsRows((prev) => {
        const next = { ...prev };
        delete next[key];
        setCmsOverrides(next);
        return next;
      });
      toast.success(t('autoSaved'));
    } catch { toast.error('Failed'); }
  }, [t, setCmsOverrides]);

  const savePerm = useCallback(async (role, key, value) => {
    const next = { ...perms, [role]: { ...(perms[role] || {}), [key]: value } };
    setPerms(next);
    try {
      await axios.put(`${API}/owner/role-permissions`, { role, permissions: next[role] }, { withCredentials: true });
      toast.success(t('autoSaved'));
    } catch { toast.error('Failed'); }
  }, [perms, t]);

  // Iter33 — VIP settings now live on their own owner endpoint (PUT /ceo/targets retired).
  const saveVip = useCallback(async () => {
    try {
      await axios.put(`${API}/owner/vip-settings`, { vip_threshold: parseInt(vipThreshold), vip_reward_program: vipRewardProgram }, { withCredentials: true });
      toast.success(t('saveChanges'));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }, [vipThreshold, vipRewardProgram, t]);

  const saveCounterFloor = useCallback(async () => {
    const v = parseInt(counterFloor, 10);
    if (Number.isNaN(v) || v < 0) { toast.error('≥ 0'); return; }
    try {
      await axios.put(`${API}/owner/counter-floor`, { counter_floor: v }, { withCredentials: true });
      setCounterFloor(v);
      toast.success(t('autoSaved'));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }, [counterFloor, t]);

  // Stripe REMOVED — not available for Albanian entities. Payments are
  // cash/POS on site + manual activation by admin. POS Bankar stays.

  const saveIntegration = useCallback(async (kind) => {
    try {
      const slug = kind === 'pos_bankar' ? 'pos-bankar' : kind;
      const payload = { ...integrations[kind] };
      // For payload, only send api_key if user actually typed one (empty string would wipe).
      if (kind !== 'pos_bankar' && !payload.api_key) delete payload.api_key;
      await axios.put(`${API}/owner/integrations/${slug}`, payload, { withCredentials: true });
      toast.success(t('saveChanges'));
      // Clear sensitive api_key locally, then refresh
      setIntegrations((prev) => ({ ...prev, [kind]: { ...prev[kind], api_key: '' } }));
      fetchAll();
    } catch { toast.error('Failed'); }
  }, [integrations, t, fetchAll]);

  // ===== Drag & drop (visual reorder of text-key list) =====
  const onDragStart = (key) => () => setDraggedKey(key);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (overKey) => (e) => {
    e.preventDefault();
    if (!draggedKey || draggedKey === overKey) return;
    setTextOrder((cur) => {
      const next = cur.filter((k) => k !== draggedKey);
      const idx = next.indexOf(overKey);
      next.splice(idx, 0, draggedKey);
      return next;
    });
    setDraggedKey(null);
  };

  const updateIntegrationField = (kind, field, value) => {
    setIntegrations((prev) => ({ ...prev, [kind]: { ...prev[kind], [field]: value } }));
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white/40">{t('loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom" data-testid="owner-console">
      {/* Header */}
      <div className="p-4 pt-6 flex items-center gap-3 sticky top-0 bg-[#050505] z-10">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="text-white/60">
          <CaretLeft size={24} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight font-['Outfit'] text-white flex items-center gap-2">
            <Crown size={22} weight="fill" className="text-[#FFD700]" />
            {t('ownerConsole')}
          </h1>
          <p className="text-xs text-white/40">Owner only</p>
        </div>
      </div>

      {/* Iter33 — customer-only console: Rolet / VIP / Vendet / Pagesat / Opsionet */}
      <Tabs value={tab} onValueChange={setTab} className="px-4">
        <TabsList className="w-full bg-[#0a0a0a] border border-white/5 rounded-2xl p-1 h-auto grid grid-cols-5">
          <TabsTrigger value="roles" className="rounded-xl text-[10px] data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="oc-tab-roles">{t('consoleRoles')}</TabsTrigger>
          <TabsTrigger value="vip" className="rounded-xl text-[10px] data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="oc-tab-vip">{t('consoleVip')}</TabsTrigger>
          <TabsTrigger value="seats" className="rounded-xl text-[10px] data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="oc-tab-seats">{t('ocSeatsTitle')}</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-xl text-[10px] data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="oc-tab-payments">{t('paymentsTitle')}</TabsTrigger>
          <TabsTrigger value="detailing" className="rounded-xl text-[10px] data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white" data-testid="oc-tab-detailing">{t('detailingTab')}</TabsTrigger>
        </TabsList>

        {/* ROLES */}
        <TabsContent value="roles" className="mt-4 space-y-3">
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-sm font-semibold text-white mb-3" style={{ color: '#0ABAB5' }}>{t('permsManagerHeader')}</p>
            <div className="space-y-2">
              {MANAGER_PERMS.map((k) => (
                <PermToggleRow
                  key={k}
                  label={t(`perm_${k}`)}
                  enabled={!!perms.manager?.[k]}
                  color="bg-[#0ABAB5]"
                  testid={`oc-perm-manager-${k}`}
                  onClick={() => savePerm('manager', k, !perms.manager?.[k])}
                />
              ))}
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-sm font-semibold text-white mb-3" style={{ color: '#FF5A00' }}>{t('permsEmployeeHeader')}</p>
            <div className="space-y-2">
              {EMPLOYEE_PERMS.map((k) => (
                <PermToggleRow
                  key={k}
                  label={t(`perm_${k}`)}
                  enabled={!!perms.employee?.[k]}
                  color="bg-[#FF5A00]"
                  testid={`oc-perm-employee-${k}`}
                  onClick={() => savePerm('employee', k, !perms.employee?.[k])}
                />
              ))}
            </div>
          </div>

          {/* Inline app texts editor (Drag & Drop reorder + click-to-edit) */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="oc-roles-texts">
            <p className="text-sm font-semibold text-white">{t('rolesAppTexts')}</p>
            <p className="text-[11px] text-white/45 mt-0.5 mb-3">{t('rolesAppTextsHelp')}</p>
            <div className="space-y-2">
              {textOrder.map((key) => {
                const row = cmsRows[key] || {};
                const isEditing = editingKey === key;
                const previewSq = row.value_sq || getDictDefault(key, 'sq');
                const overridden = !!(row.value_sq || row.value_en || row.value_it);
                return (
                  <div
                    key={key}
                    draggable
                    onDragStart={onDragStart(key)}
                    onDragOver={onDragOver}
                    onDrop={onDrop(key)}
                    className={`rounded-xl bg-[#050505] border border-white/[0.06] transition ${draggedKey === key ? 'opacity-50' : ''}`}
                    data-testid={`oc-text-row-${key}`}
                  >
                    <button
                      type="button"
                      onClick={() => setEditingKey(isEditing ? null : key)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                    >
                      <DotsSixVertical size={16} className="text-white/30 shrink-0 cursor-grab" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-white/40">{key}</p>
                        <p className="text-sm text-white truncate">{previewSq}</p>
                      </div>
                      {overridden && (
                        <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-[#0ABAB5]/15 text-[#0ABAB5]">edit</span>
                      )}
                    </button>
                    {isEditing && (
                      <div className="px-3 pb-3 pt-1 space-y-2 border-t border-white/[0.06]">
                        {['sq', 'en', 'it'].map((lang) => {
                          const placeholder = getDictDefault(key, lang);
                          return (
                            <div key={lang} className="flex items-center gap-2">
                              <span className="text-[10px] uppercase text-white/40 w-6">{lang}</span>
                              <Input
                                defaultValue={row[`value_${lang}`] || ''}
                                placeholder={placeholder}
                                onBlur={(e) => {
                                  if (e.target.value !== (row[`value_${lang}`] || '')) saveCmsText(key, lang, e.target.value);
                                }}
                                className="h-9 bg-[#0a0a0a] border-white/10 rounded-lg text-xs placeholder:text-white/30"
                                data-testid={`oc-text-${key}-${lang}`}
                              />
                            </div>
                          );
                        })}
                        {overridden && (
                          <button
                            type="button"
                            onClick={() => resetCmsText(key)}
                            className="text-[10px] text-white/50 hover:text-red-400 flex items-center gap-1 mt-1"
                            data-testid={`oc-text-reset-${key}`}
                          >
                            <XIcon size={11} /> Reset to default
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Iter35 — weekly-wash counter minimum: HomePage shows max(this, real washes last 7 days) */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="oc-counter-floor-card">
            <label className="text-sm font-semibold text-white block mb-2">{t('counterFloorLabel')}</label>
            <div className="flex gap-2">
              <Input
                type="number"
                min={0}
                value={counterFloor}
                onChange={(e) => setCounterFloor(e.target.value)}
                className="h-11 bg-[#050505] border-white/10 rounded-xl text-sm"
                data-testid="oc-counter-floor-input"
              />
              <Button onClick={saveCounterFloor} className="cta-button-primary h-11 px-4 rounded-xl font-semibold" data-testid="oc-counter-floor-save">
                <FloppyDisk size={16} />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* VIP */}
        <TabsContent value="vip" className="mt-4 space-y-3">
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5 space-y-3">
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">{t('vipThresholdLabel')}</label>
              <Input
                type="number"
                value={vipThreshold}
                onChange={(e) => setVipThreshold(e.target.value)}
                className="h-12 bg-[#050505] border-white/10 rounded-xl"
                data-testid="oc-vip-threshold"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">{t('vipRewardProgram')}</label>
              <select
                value={vipRewardProgram}
                onChange={(e) => setVipRewardProgram(e.target.value)}
                className="w-full h-12 bg-[#050505] border border-white/10 rounded-xl px-3 text-sm text-white"
                data-testid="oc-vip-reward"
              >
                {programs.map((p) => (
                  <option key={p.program_id} value={p.program_id} className="bg-[#0a0a0a]">{p.name_en}</option>
                ))}
              </select>
            </div>
            <Button onClick={saveVip} className="cta-button-primary w-full h-12 rounded-xl font-semibold" data-testid="oc-vip-save">
              <FloppyDisk size={16} className="mr-2" /> {t('saveChanges')}
            </Button>
          </div>
        </TabsContent>

        {/* SEATS — Vendet e Abonimeve: capacity/threshold configuration per plan. */}
        <TabsContent value="seats" className="mt-4 space-y-3">
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <p className="text-sm font-semibold text-white mb-1">{t('ocSeatsTitle')}</p>
            <p className="text-[11px] text-white/45 leading-snug mb-3">{t('ocSeatsHelp')}</p>
            <div className="space-y-2.5" data-testid="oc-seats-list">
              {seats.map((s) => {
                const color = s.status === 'full' || s.status === 'danger' ? '#ef4444'
                            : s.status === 'warn' ? '#FFD700'
                            : '#22C55E';
                return (
                  <div key={s.plan_id}
                       className="p-3 rounded-2xl bg-white/[0.03] border border-white/5"
                       data-testid={`oc-seat-${s.plan_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-white">{s.plan_id.replace('plan_', '').replace('_', ' ').toUpperCase()}</p>
                      <span className="text-xs font-bold" style={{ color }}>{s.free}/{s.capacity}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {['capacity', 'free', 'warn_threshold', 'danger_threshold'].map((k) => (
                        <div key={k}>
                          <Input
                            type="number"
                            defaultValue={s[k]}
                            onBlur={async (e) => {
                              const v = parseInt(e.target.value);
                              if (Number.isNaN(v)) return;
                              if (v === s[k]) return;
                              try {
                                await axios.put(`${API}/owner/subscription-seats`, { [s.plan_id]: { [k]: v } }, { withCredentials: true });
                                const refreshed = await axios.get(`${API}/subscription-seats`);
                                setSeats(refreshed.data || []);
                                toast.success(t('autoSaved'));
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed');
                              }
                            }}
                            className="h-9 bg-[#050505] border-white/10 rounded-lg text-xs text-center"
                            data-testid={`oc-seat-${s.plan_id}-${k}`}
                          />
                          <p className="text-[9px] text-white/40 text-center mt-0.5 leading-tight">
                            {k === 'capacity' ? t('capacity') : k === 'free' ? t('freeSeats') : k === 'warn_threshold' ? t('warnThreshold') : t('dangerThreshold')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* PAYMENTS */}
        <TabsContent value="payments" className="mt-4 space-y-3">
          <div className="p-3 rounded-xl bg-[#0ABAB5]/8 border border-[#0ABAB5]/25 text-[11px] text-[#0ABAB5] leading-snug">
            <p className="font-semibold mb-0.5">{t('paymentsAppBlock')}</p>
            <p className="text-[#0ABAB5]/80">{t('paymentsAppHelp')}</p>
          </div>

          {/* RAIFFEISEN GATEWAY */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5 space-y-3" data-testid="oc-raiffeisen-card">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard size={18} weight="fill" className="text-[#FF5A00]" />
              <p className="text-sm font-semibold text-white flex-1">{t('raiffeisenGateway')}</p>
              <Toggle
                on={raiffeisenEnabled}
                onToggle={() => setRaiffeisenEnabled(!raiffeisenEnabled)}
                testid="oc-raiffeisen-toggle"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">{t('raiffeisenMerchantId')}</label>
              <Input
                value={raiffeisenMerchant}
                onChange={(e) => setRaiffeisenMerchant(e.target.value)}
                placeholder="MID-12345"
                className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                data-testid="oc-raiffeisen-mid"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">{t('raiffeisenApiKey')}</label>
              {raiffeisenKeySet && !raiffeisenApiKey && (
                <p className="text-[10px] text-[#FF5A00] mb-1">✓ {t('secretSet')}</p>
              )}
              <Input
                type="password"
                value={raiffeisenApiKey}
                onChange={(e) => setRaiffeisenApiKey(e.target.value)}
                placeholder={raiffeisenKeySet ? 'Update key…' : 'RAIF-***'}
                className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                data-testid="oc-raiffeisen-key"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">{t('raiffeisenWebhookUrl')}</label>
              <Input
                value={raiffeisenWebhook}
                onChange={(e) => setRaiffeisenWebhook(e.target.value)}
                placeholder="https://..."
                className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                data-testid="oc-raiffeisen-webhook"
              />
            </div>
            <Button
              onClick={async () => {
                try {
                  const payload = {
                    raiffeisen_enabled: raiffeisenEnabled,
                    raiffeisen_merchant_id: raiffeisenMerchant,
                    raiffeisen_webhook_url: raiffeisenWebhook,
                  };
                  if (raiffeisenApiKey) payload.raiffeisen_api_key = raiffeisenApiKey;
                  const r = await axios.put(`${API}/owner/raiffeisen-config`, payload, { withCredentials: true });
                  setRaiffeisenEnabled(!!r.data.raiffeisen_enabled);
                  setRaiffeisenKeySet(!!r.data.raiffeisen_api_key_set);
                  setRaiffeisenApiKey('');
                  toast.success(t('autoSaved'));
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed');
                }
              }}
              className="cta-button-primary w-full h-11 rounded-xl font-semibold"
              data-testid="oc-raiffeisen-save"
            >
              <FloppyDisk size={16} className="mr-2" /> {t('saveChanges')}
            </Button>
          </div>

          {/* Cash always-on info card */}
          <div className="p-3 rounded-2xl bg-[#FFD700]/8 border border-[#FFD700]/25 flex items-center gap-2" data-testid="oc-cash-info">
            <Coins size={16} weight="fill" className="text-[#FFD700]" />
            <p className="text-[12px] text-[#FFD700] font-semibold">{t('cashAlwaysOn')}</p>
          </div>

          {/* AWOARA */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5 space-y-3" data-testid="oc-awoara-card">
            <div className="flex items-center gap-2 mb-1">
              <Bank size={18} weight="fill" className="text-[#FFD700]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{t('awoaraTitle')}</p>
                <p className="text-[10px] text-white/40">{t('awoaraDesc')}</p>
              </div>
              <Toggle
                on={integrations.awoara.enabled}
                onToggle={() => updateIntegrationField('awoara', 'enabled', !integrations.awoara.enabled)}
                testid="oc-awoara-toggle"
              />
            </div>
            <Input value={integrations.awoara.merchant_id || ''}
                   onChange={(e) => updateIntegrationField('awoara', 'merchant_id', e.target.value)}
                   placeholder={t('fieldMerchantId')}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-awoara-mid" />
            <Input value={integrations.awoara.api_key || ''}
                   onChange={(e) => updateIntegrationField('awoara', 'api_key', e.target.value)}
                   placeholder={integrations.awoara.api_key_set ? '✓ ' + t('secretSet') : t('fieldApiKey')}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-awoara-key" />
            <Input value={integrations.awoara.webhook_url || ''}
                   onChange={(e) => updateIntegrationField('awoara', 'webhook_url', e.target.value)}
                   placeholder={t('fieldWebhookUrl')}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-awoara-webhook" />
            <Button onClick={() => saveIntegration('awoara')} className="cta-button-primary w-full h-11 rounded-xl font-semibold" data-testid="oc-awoara-save">
              <FloppyDisk size={16} className="mr-2" /> {t('saveChanges')}
            </Button>
          </div>

          {/* ANPR */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5 space-y-3" data-testid="oc-anpr-card">
            <div className="flex items-center gap-2 mb-1">
              <Camera size={18} weight="fill" className="text-[#0ABAB5]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{t('anprTitle')}</p>
                <p className="text-[10px] text-white/40">{t('anprDesc')}</p>
              </div>
              <Toggle
                on={integrations.anpr.enabled}
                onToggle={() => updateIntegrationField('anpr', 'enabled', !integrations.anpr.enabled)}
                testid="oc-anpr-toggle"
              />
            </div>
            <Input value={integrations.anpr.camera_ip || ''}
                   onChange={(e) => updateIntegrationField('anpr', 'camera_ip', e.target.value)}
                   placeholder={t('fieldCameraIp') + ' (192.168.1.x)'}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-anpr-ip" />
            <Input value={integrations.anpr.api_key || ''}
                   onChange={(e) => updateIntegrationField('anpr', 'api_key', e.target.value)}
                   placeholder={integrations.anpr.api_key_set ? '✓ ' + t('secretSet') : t('fieldApiKey')}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-anpr-key" />
            <Input value={integrations.anpr.endpoint_url || ''}
                   onChange={(e) => updateIntegrationField('anpr', 'endpoint_url', e.target.value)}
                   placeholder={t('fieldEndpointUrl')}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-anpr-endpoint" />
            <Button onClick={() => saveIntegration('anpr')} className="cta-button-primary w-full h-11 rounded-xl font-semibold" data-testid="oc-anpr-save">
              <FloppyDisk size={16} className="mr-2" /> {t('saveChanges')}
            </Button>
          </div>

          {/* POS BANKAR */}
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5 space-y-3" data-testid="oc-pos-card">
            <div className="flex items-center gap-2 mb-1">
              <Bank size={18} weight="fill" className="text-[#FF5A00]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{t('posBankarTitle')}</p>
                <p className="text-[10px] text-white/40">{t('posBankarDesc')}</p>
              </div>
              <Toggle
                on={integrations.pos_bankar.enabled}
                onToggle={() => updateIntegrationField('pos_bankar', 'enabled', !integrations.pos_bankar.enabled)}
                testid="oc-pos-toggle"
              />
            </div>
            <Input value={integrations.pos_bankar.bank_name || ''}
                   onChange={(e) => updateIntegrationField('pos_bankar', 'bank_name', e.target.value)}
                   placeholder={t('fieldBankName') + ' (BKT / Credins / Raiffeisen)'}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs"
                   data-testid="oc-pos-bank" />
            <Input value={integrations.pos_bankar.terminal_id || ''}
                   onChange={(e) => updateIntegrationField('pos_bankar', 'terminal_id', e.target.value)}
                   placeholder={t('fieldTerminalId')}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-pos-tid" />
            <Input value={integrations.pos_bankar.merchant_id || ''}
                   onChange={(e) => updateIntegrationField('pos_bankar', 'merchant_id', e.target.value)}
                   placeholder={t('fieldMerchantId') + ' (MID)'}
                   className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                   data-testid="oc-pos-mid" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={integrations.pos_bankar.host_ip || ''}
                     onChange={(e) => updateIntegrationField('pos_bankar', 'host_ip', e.target.value)}
                     placeholder={t('fieldHostIp')}
                     className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                     data-testid="oc-pos-host" />
              <Input value={integrations.pos_bankar.port || ''}
                     onChange={(e) => updateIntegrationField('pos_bankar', 'port', parseInt(e.target.value) || 0)}
                     placeholder={t('fieldPort')}
                     type="number"
                     className="h-11 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                     data-testid="oc-pos-port" />
            </div>
            <Button onClick={() => saveIntegration('pos_bankar')} className="cta-button-primary w-full h-11 rounded-xl font-semibold" data-testid="oc-pos-save">
              <FloppyDisk size={16} className="mr-2" /> {t('saveChanges')}
            </Button>
          </div>
        </TabsContent>

        {/* Opsionet — Liquid Glass / Detailing configuration (customer-facing services) */}
        <TabsContent value="detailing" className="mt-4 space-y-3" data-testid="oc-detailing-content">
          <DetailingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
